import { useEffect, useState } from 'react';
import keycloak from '@/config/keycloak';
import { useUserProfile } from '@/services/user/hooks';
import { formatDurationMs } from '@/utils/formatters';
import { Badge } from '@/components/ui/badge';
import { CopyButton } from '@/components/ui/CopyButton';
import JSONViewer from '@/components/ui/JSONViewer';
import { EmbeddedAuthTriggers } from '@/components/DevPanel/EmbeddedAuthTriggers';

// Ticks once a second while mounted. This component only mounts when the Auth
// tab is active (Radix unmounts inactive tab content), so the interval is idle
// whenever the panel is closed or another tab is showing.
function useSecondsUntil(exp: number | undefined): number | undefined {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  return exp === undefined ? undefined : exp - now;
}

function formatCountdown(secs: number | undefined): string {
  if (secs === undefined) return '—';
  if (secs <= 0) return 'expired';
  return formatDurationMs(secs * 1000);
}

function RoleGroup({ label, roles }: { label: string; roles: string[] }) {
  return (
    <div className="flex items-start gap-2">
      <span className="w-14 shrink-0 pt-0.5 text-[11px] text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-1">
        {roles.length ? (
          roles.map((r) => (
            <Badge key={r} variant="secondary" className="font-mono text-[10px]">
              {r}
            </Badge>
          ))
        ) : (
          <span className="text-[11px] text-muted-foreground">none</span>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 break-words font-mono text-[11px]">{children}</span>
    </div>
  );
}

export function AuthInspector() {
  const profile = useUserProfile();
  const parsed = keycloak.tokenParsed;
  const accessLeft = useSecondsUntil(parsed?.exp);

  const tokenRoles = parsed?.realm_access?.roles ?? [];
  const profileRoles = profile.roles ?? [];

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-1.5">
        <h3 className="text-xs font-semibold text-muted-foreground">Identity</h3>
        <Field label="Username">{parsed?.preferred_username ?? '—'}</Field>
        <Field label="Email">{parsed?.email ?? profile.email ?? '—'}</Field>
        <Field label="Subject">{parsed?.sub ?? '—'}</Field>
        <Field label="Team">
          {profile.teamName ? `${profile.teamName} (${profile.teamId})` : '—'}
        </Field>
      </section>

      <section className="flex flex-col gap-1.5">
        <h3 className="text-xs font-semibold text-muted-foreground">Token lifetime</h3>
        <Field label="Access in">
          <span className={accessLeft !== undefined && accessLeft <= 30 ? 'text-destructive' : ''}>
            {formatCountdown(accessLeft)}
          </span>
        </Field>
        <div className="mt-1 flex flex-wrap gap-2">
          <CopyButton value={keycloak.token} label="Copy bearer" />
          <CopyButton
            value={parsed ? JSON.stringify(parsed, null, 2) : undefined}
            label="Copy claims"
          />
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold text-muted-foreground">
          Roles
          <span className="ml-1.5 font-normal opacity-60">token vs profile</span>
        </h3>
        <p className="text-[11px] text-muted-foreground">
          Capabilities derive from the <span className="font-medium">profile</span> roles (from the
          API), not the token. Drift between the two is worth noticing.
        </p>
        <div className="flex flex-col gap-1.5">
          <RoleGroup label="Token" roles={tokenRoles} />
          <RoleGroup label="Profile" roles={profileRoles} />
        </div>
      </section>

      <EmbeddedAuthTriggers />

      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold text-muted-foreground">Decoded claims</h3>
        {parsed ? (
          <JSONViewer data={parsed} collapsedHeight={280} />
        ) : (
          <p className="text-[11px] text-muted-foreground">No parsed token available.</p>
        )}
      </section>
    </div>
  );
}
